import React, { useEffect, useState } from "react";
import { JewelryConfiguratorComponentsProps, OptionStyle } from "./types";
import SquareIconButton from "@components/atoms/JewelryBuilderOptions/SquareIconButton";
import RectangularTextButton from "@components/atoms/JewelryBuilderOptions/RectangularTextButton";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { DisplayConfigurationV2 } from "@src/types/DiamondDetail";
import JewelryBuilderSlider from "@components/atoms/JewelryBuilderOptions/JewelryBuilderSlider";
import RectangularTextButtonWithPrice from "@components/atoms/JewelryBuilderOptions/RectangularTextButtonWithPrice";
import JewelryBuilderDropDown from "@components/atoms/JewelryBuilderOptions/JewelryBuilderDropDown";
import { fetchComponentSize } from "./utils";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections } from "@utils/customization";
import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";
import JewelryBuilderMeasurementInput from "@components/atoms/JewelryBuilderOptions/JewelryBuilderMeasurementInput";
import JewelryBuilderCheckBox from "@components/atoms/JewelryBuilderOptions/JewelryBuilderCheckBox";
import JewelryBuilderCheckBoxWithDropDown from "@components/atoms/JewelryBuilderOptions/JewelryBuilderCheckBoxWithDropDown";
import JewelryBuilderMultiSelectCheckBoxWithDropDown from "@components/atoms/JewelryBuilderOptions/JewelryBuilderMultiSelectCheckBoxWithDropDown";

const JewelryConfiguratorComponents = ({
  displayConfigurationV2,
  productType,
  maxWidth,
  fromReviewPage = false,
  fromOrderCart,
  fromRequest,
  enterSecondFlow = false,
  updateJewelryConfigInfo,
  onDropdownSelect,
}: JewelryConfiguratorComponentsProps) => {
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const mDisplayConfigV2 = displayConfigurationV2.filter(
    (mOption) =>
      !(mEnterSecondFlow === true && mOption.option_type === "supported_shapes")
  );

  const jewelryConfiguratorLayout = useGetPdpSectionLayout(
    PdpSections.JEWELRY_CONFIGURATOR,
    productType
  );

  const [openStates, setOpenStates] = useState<{ [key: string]: boolean }>({});
  const getKey = (item: DisplayConfigurationV2, index: number) =>
    `${item.option_style}_${index}`;

  // Hidden options (option_visible === false) still need their default value
  // applied to the configurator state. This MUST happen as a side effect, not
  // during render: calling `updateJewelryConfigInfo` (which sets React state)
  // while rendering triggers an infinite render loop / page hang on web.
  const hiddenOptions = mDisplayConfigV2.filter(
    (option) => option.option_visible === false
  );
  const hiddenOptionsSignature = hiddenOptions
    .map(
      (option) => `${option?.option_type}:${option?.option_value?.[0]?.value}`
    )
    .join("|");

  useEffect(() => {
    if (!hiddenOptionsSignature) return;
    hiddenOptions.forEach((option) => {
      updateJewelryConfigInfo(option?.option_type, [
        option,
        option?.option_value[0].value,
      ]);
    });
    // Intentionally keyed only on the hidden-option signature so this runs once
    // per unique hidden-option set and never re-triggers itself.
  }, [hiddenOptionsSignature]);

  const toggleOpenState = (key: string) => {
    setOpenStates((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderComponent = (item: DisplayConfigurationV2, index: number) => {
    const componentStyle = fetchComponentSize(item?.option_style);
    const key = getKey(item, index);
    const isOpen = openStates[key] || false;
    if (item.option_visible === false) {
      // Default value for hidden options is applied via useEffect above.
      return null;
    }
    switch (item.option_style) {
      case OptionStyle.slider:
      case OptionStyle.sliderDefaultNoCarousel:
        return (
          <JewelryBuilderSlider
            productType={productType}
            configObject={item}
            fromReviewPage={fromReviewPage}
            isOpen={isOpen}
            onToggle={() => toggleOpenState(key)}
            fromOrderCart={fromOrderCart}
            fromRequest={fromRequest}
            enterSecondFlow={mEnterSecondFlow}
            onSelect={onDropdownSelect}
          />
        );
      case OptionStyle.dropDown:
      case OptionStyle.dropDownSelect:
      case OptionStyle.dropdownDefaultNoCarousel:
        return (
          <JewelryBuilderDropDown
            productType={productType}
            configObject={item}
            fromReviewPage={fromReviewPage}
            isOpen={isOpen}
            onToggle={() => toggleOpenState(key)}
            fromOrderCart={fromOrderCart}
            fromRequest={fromRequest}
            enterSecondFlow={mEnterSecondFlow}
            onSelect={onDropdownSelect}
          />
        );
      case OptionStyle.mediaButtonLabeledNoCarousel:
      case OptionStyle.mediaButtonLabeledWithCarousel:
      case OptionStyle.mediaButtonCompactNoCarousel:
      case OptionStyle.mediaButtonCompactWithCarousel:
      case OptionStyle.mediaButtonLabeled2LineNoCarousel:
      case OptionStyle.mediaButtonLabeled2LineWithCarousel:
      case OptionStyle.squareIconButton:
      case OptionStyle.bgSquareIconButton:
      case OptionStyle.squareIconButtonWithBG:
      case OptionStyle.squareIconButtonWithSlider:
      case OptionStyle.squareIconButtonWithSliderAndBG:
        return (
          <SquareIconButton
            configObject={item}
            productType={productType}
            maxWidth={maxWidth}
            buttonSize={componentStyle.buttonSize}
            showCarousel={componentStyle.showCarousel}
            fromReviewPage={fromReviewPage}
            isOpen={isOpen}
            onToggle={() => toggleOpenState(key)}
            fromOrderCart={fromOrderCart}
            fromRequest={fromRequest}
          />
        );
      case OptionStyle.textButtonDynamicNoCarousel:
      case OptionStyle.textButtonDynamicWithCarousel:
      case OptionStyle.rectangularTextButton:
      case OptionStyle.roundTextButton:
        return (
          <RectangularTextButton
            productType={productType}
            configObject={item}
            showCarousel={componentStyle.showCarousel}
            fromReviewPage={fromReviewPage}
            isOpen={isOpen}
            onToggle={() => toggleOpenState(key)}
            fromOrderCart={fromOrderCart}
            fromRequest={fromRequest}
            enterSecondFlow={mEnterSecondFlow}
            onSelect={onDropdownSelect}
          />
        );
      case OptionStyle.textButton2LineNoCarousel:
      case OptionStyle.textButton2LineWithCarousel:
      case OptionStyle.textButtonWithPriceNoCarousel:
      case OptionStyle.textButtonWithPriceWithCarousel:
      case OptionStyle.rectangularTextButtonWithPrice:
        return (
          <RectangularTextButtonWithPrice
            productType={productType}
            configObject={item}
            showCarousel={componentStyle.showCarousel}
            fromReviewPage={fromReviewPage}
            isOpen={isOpen}
            onToggle={() => toggleOpenState(key)}
            fromOrderCart={fromOrderCart}
            fromRequest={fromRequest}
          />
        );
      case OptionStyle.checkBoxWithDropDown:
      case OptionStyle.radioButtonWithDropDown:
        return (
          <JewelryBuilderCheckBoxWithDropDown
            productType={productType}
            configObject={item}
            isRadioButton={item.option_style.includes("radio")}
          />
        );
      case OptionStyle.multiSelectCheckBoxWithDropDown:
      case OptionStyle.selectRadioButtonWithDropDown:
        return (
          <JewelryBuilderMultiSelectCheckBoxWithDropDown
            productType={productType}
            configObject={item}
            isRadioButton={item.option_style.includes("radio")}
          />
        );
      case OptionStyle.checkBox:
      case OptionStyle.radioButton:
        return (
          <JewelryBuilderCheckBox
            productType={productType}
            configObject={item}
            isRadioButton={item.option_style.includes("radio")}
          />
        );
      case OptionStyle.centerMeasurement:
        return (
          <JewelryBuilderMeasurementInput
            productType={productType}
            configObject={item}
          />
        );
    }
  };

  return mDisplayConfigV2.map((item, index) => {
    if (item.option_style) {
      return (
        <>
          {index !== 0 &&
            index !== displayConfigurationV2.length &&
            item.option_visible && (
              <Spacer
                size={
                  jewelryConfiguratorLayout.variation_padding
                    ? jewelryConfiguratorLayout.variation_padding
                    : SpaceType.Basic
                }
              />
            )}
          {renderComponent(item, index)}
        </>
      );
    }
    return <></>;
  });
};

export default JewelryConfiguratorComponents;
